import { isDev } from '../../../../core/utils/is-node-environment.js';
import { RESIZE_HANDLE_SIZE } from '../Resizer.js';
import {
  panelSizeUnits,
  PanelSizeUnit,
  PanelSizesWithUnit,
  PanelSizes,
} from '../types/shared-panel-types.js';

export type ParsedValue = {
  value: number;
  unit: PanelSizeUnit;
};

/** Type guard for supported units. */
function isSupportedUnit(value: unknown): value is PanelSizeUnit {
  return panelSizeUnits.includes(value as PanelSizeUnit);
}

/** Parse the given value to get the numeric value and unit. */
export function parseValue(
  value: string | number | undefined,
): ParsedValue | null {
  if (!value) {
    return null;
  }

  if (typeof value === 'number') {
    return {
      value,
      unit: 'px',
    };
  }
  const [, parsedValue, parsedUnit] = value.match(/(-?[\d.]+)([a-z%]*)/) || [];

  if (!parsedValue || !isSupportedUnit(parsedUnit)) {
    return null;
  }

  const numericValue = parseFloat(parsedValue);

  return {
    value: numericValue,
    unit: parsedUnit as PanelSizeUnit,
  };
}

//#region fractions
/** Convert the given amount of fractions to pixels. */
export function fractionsToPx(
  fractions: number,
  contextWidth: number,
  resizeHandleCount: number,
): number {
  return (
    Math.round(
      // When converting fractions to pixels, we need to subtract all the resize handles from the currently available space.
      (contextWidth - resizeHandleCount * RESIZE_HANDLE_SIZE) * fractions,
    ) / 100
  );
}

/** Convert the given amount of pixels to fractions. In the panel calculation fractions always scale to a 100, it can also be used to convert px into percent. */
export function pxToFractions(
  px: number,
  contextWidth: number,
  resizeHandleCount: number,
): number {
  return (
    Math.round(
      // When converting fractions to pixels, we need to subtract all the resize handles from the currently available space.
      (px / (contextWidth - resizeHandleCount * RESIZE_HANDLE_SIZE)) * 10000,
    ) / 100
  );
}
//#endregion

//#region view width
/** Convert view width to pixels. */
export function vwToPx(vw: number): number {
  return Math.round((window.innerWidth * vw) / 100);
}

/** Convert pixels to view width. */
export function pxToVw(px: number): number {
  return Math.ceil((px / window.innerWidth) * 10000) / 100;
}
//#endregion

//#region rem
// The fontSize is a string containing the unit (px) and with parseFloat it will only return the numeric value
const _getOneRemInPx = () =>
  parseFloat(window.getComputedStyle(document.documentElement).fontSize);

/** Convert rem to pixels. */
export function remToPx(rem: number): number {
  return rem * _getOneRemInPx();
}

/** Convert pixels to rem. */
export function pxToRem(px: number): number {
  return Math.ceil(px / _getOneRemInPx());
}
//#endregion

/** Converts sizes in fractions to an object with panel sizes in all available units. */
export function convertToPanelSizes(
  sizes: PanelSizesWithUnit,
  rootWidth: number,
  resizeHandlesCount: number,
): PanelSizes {
  return Object.entries(sizes).reduce((acc: PanelSizes, [key, parsedValue]) => {
    const valuePx =
      anyUnitToPx(parsedValue, rootWidth, resizeHandlesCount) ?? 0;

    const percentageValue = pxToFractions(
      valuePx,
      rootWidth,
      resizeHandlesCount,
    );

    acc[key] = {
      px: valuePx,
      '%': percentageValue,
    };

    return acc;
  }, {});
}

/** Converts widths into px. It handles widths in px, fr, %, vw, and rem. */
export function anyUnitToPx(
  parsedValue: ParsedValue,
  rootWidth: number,
  resizeHandlesCount: number,
  /** If true, it doesn't convert 100% widths to fractions and returns null instead. */
  ignoreFullWidth?: boolean,
) {
  switch (parsedValue.unit) {
    case 'px':
      return parsedValue.value;
    case 'fr':
      return fractionsToPx(parsedValue.value, rootWidth, resizeHandlesCount);
    case '%':
      // Panels with 100% width are considered to share whatever space is left in the container.
      return ignoreFullWidth && parsedValue.value === 100
        ? null
        : fractionsToPx(parsedValue.value, rootWidth, resizeHandlesCount);
    case 'vw':
      return vwToPx(parsedValue.value);
    case 'rem':
      return remToPx(parsedValue.value);
    default: {
      if (isDev()) {
        // eslint-disable-next-line no-console
        console.warn(
          'You might have forgotten to add a new supported unit for the preferredWidth.',
        );
      }
      return null;
    }
  }
}

/** Converts widths into fr. It handles widths in px, fr, %,vw and rem. */
export function anyUnitToFractions(
  parsedValue: ParsedValue,
  rootWidth: number,
  resizeHandlesCount: number,
  /** If true, it doesn't convert 100% widths to fractions and returns null instead. */
  ignoreFullWidth?: boolean,
) {
  // Safeguard to avoid runtime errors.
  if (parsedValue === undefined) {
    return null;
  }

  switch (parsedValue.unit) {
    case 'px':
      return pxToFractions(parsedValue.value, rootWidth, resizeHandlesCount);
    case 'fr':
      return parsedValue.value;
    case '%':
      // Panels with 100% width are considered to share whatever space is left in the container.
      return ignoreFullWidth && parsedValue.value === 100
        ? null
        : parsedValue.value;
    case 'vw':
      return pxToFractions(
        vwToPx(parsedValue.value),
        rootWidth,
        resizeHandlesCount,
      );
    case 'rem':
      return pxToFractions(
        remToPx(parsedValue.value),
        rootWidth,
        resizeHandlesCount,
      );
    default: {
      if (isDev()) {
        // eslint-disable-next-line no-console
        console.warn(
          'You might have forgotten to add a new supported unit for the preferredWidth.',
        );
      }
      return null;
    }
  }
}

/** Converts fractions to the target unit. */
export function fractionsToTargetUnit(
  value: number,
  targetUnit: PanelSizeUnit,
  rootWidth: number,
  resizeHandlesCount: number,
) {
  switch (targetUnit) {
    case 'px':
      return fractionsToPx(value, rootWidth, resizeHandlesCount);
    case 'fr':
    case '%':
      return value;
    case 'vw':
      return pxToVw(fractionsToPx(value, rootWidth, resizeHandlesCount));
    case 'rem':
      return pxToRem(fractionsToPx(value, rootWidth, resizeHandlesCount));
    default: {
      if (isDev()) {
        // eslint-disable-next-line no-console
        console.warn(
          'You might have forgotten to add a new supported unit for the preferredWidth.',
        );
      }
      return null;
    }
  }
}

/** Convert the passed width into a px based one. */
export function panelPropWidthToPx(
  width: string | number | undefined,
  rootWidth: number,
  resizeHandlesCount: number = 0,
): number | null {
  const parsedPreferredWidth = parseValue(width);

  if (!parsedPreferredWidth) {
    return null;
  }

  return anyUnitToPx(parsedPreferredWidth, rootWidth, resizeHandlesCount, true);
}
